import axios from "axios";

function computeBaseUrl() {
  const isTelegram = typeof window !== "undefined" && !!window.Telegram?.WebApp;
  if (isTelegram) return "/telegramMiniApp";
  return process.env.REACT_APP_MINIAPP_API_BASE_URL || "/telegramMiniApp";
}

const API_BASE_URL = computeBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

let authToken = null;

const handleApiError = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.error || error?.message || "Request failed. Please try again.";
  const err = new Error(message);
  err.status = status;
  err.data = error?.response?.data;
  throw err;
};

export const setAuthToken = (token) => {
  authToken = token || null;
};

export const clearAuthToken = () => {
  authToken = null;
};

apiClient.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

export const sendOtp = async (email, role = "rider") => {
  try {
    const response = await apiClient.post("/auth/send-otp", { email: email.trim().toLowerCase(), role });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const verifyOtp = async (email, otp, role = "rider") => {
  try {
    const response = await apiClient.post("/auth/verify-otp", { email: email.trim().toLowerCase(), otp: String(otp).trim(), role });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const verifyUser = async (initData) => {
  try {
    const response = await apiClient.post("/verifyTelegramUser", { initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const registerRider = async (initData, payload) => {
  try {
    const response = await apiClient.post("/createRider", { initData, ...payload });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const deleteRiderAccount = async (initData) => {
  try {
    const response = await apiClient.delete("/deleteRider", { data: { initData } });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const updateRiderProfile = async (initData, payload) => {
  try {
    const response = await apiClient.post("/rider/profile/update", { initData, ...payload });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getRideHistory = async (telegramId) => {
  try {
    const response = await apiClient.get(`/rider/${encodeURIComponent(telegramId)}/rides`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getActiveRide = async (telegramId) => {
  try {
    const response = await apiClient.get(`/rider/${encodeURIComponent(telegramId)}/active-ride`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getMatchedRide = async (telegramId) => {
  try {
    const response = await apiClient.get(`/rider/${encodeURIComponent(telegramId)}/matched-ride`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const suggestAddresses = async (q) => {
  try {
    const response = await apiClient.get(`/suggest`, { params: { q } });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const requestRide = async ({ initData, pickup, dropoff, rideTime, bid = 0, notes }) => {
  try {
    const response = await apiClient.post(`/rider/request`, { initData, pickup, dropoff, rideTime, bid, notes });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const listRideBids = async (rideId, initData) => {
  try {
    const response = await apiClient.post(`/rides/${encodeURIComponent(rideId)}/bids`, { initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const acceptBid = async (bidId, initData) => {
  try {
    const response = await apiClient.post(`/bids/accept`, { bidId, initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const rejectBid = async (bidId, initData) => {
  try {
    const response = await apiClient.post(`/bids/reject`, { bidId, initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const updateRideDetails = async (rideId, initData, payload) => {
  try {
    const response = await apiClient.post(`/rides/${encodeURIComponent(rideId)}/update`, { initData, ...payload });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const cancelRide = async (rideId, initData) => {
  try {
    const response = await apiClient.post(`/rides/${encodeURIComponent(rideId)}/cancel`, { initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const completeRideByRider = async (rideId, initData) => {
  try {
    const response = await apiClient.post(`/rides/${encodeURIComponent(rideId)}/complete-rider`, { initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export default { verifyUser, registerRider, deleteRiderAccount, updateRiderProfile, getRideHistory, getActiveRide, getMatchedRide, suggestAddresses, requestRide, listRideBids, acceptBid, rejectBid, updateRideDetails, cancelRide, completeRideByRider };
